/**
 * El oficio de un requerimiento.
 *
 * Sale del sistema en PDF, sin número: se numera y se firma en el trámite
 * documentario. Vuelve firmado y se registra aquí con su número, que desde
 * ahí es el del oficio. Hasta entonces el envío espera: lo que se entrega es
 * el oficio firmado, no un borrador.
 */
import { useState } from 'react'
import { Button } from '@mpfn/ui'
import { Callout } from '@mpfn/patterns'
import { FileText } from 'lucide-react'
import { useRegistrarOficioFirmado } from '../api/consultas.js'
import { urlDeArchivo, urlDeProyectoOficio } from '../api/cliente.js'
import type { Requerimiento } from '../api/tipos.js'
import { usePuede } from '../sesion/contexto.js'
import { avisarError, avisarExito } from '../lib/avisos.js'
import { fecha } from '../lib/formato.js'
import { DialogoFirmado } from './DialogoFirmado.js'

export function OficioDelRequerimiento({ requerimiento }: { requerimiento: Requerimiento }) {
  const puedeRegistrar = usePuede('requerimiento.emitir')
  const registrar = useRegistrarOficioFirmado()
  const [registrando, setRegistrando] = useState(false)
  const { oficio } = requerimiento

  if (!oficio.lleva || requerimiento.anulada) return null

  const destinatario = [oficio.destinatarioNombre, oficio.destinatarioCargo].filter(Boolean).join(', ')

  if (oficio.firmado) {
    return (
      <div className="sigfisc-acciones sigfisc-oficio-firmado">
        <p className="sigfisc-nota">
          <strong>Oficio N.° {oficio.numeroTramite}</strong>
          {oficio.expedienteTramite ? ` · expediente ${oficio.expedienteTramite}` : ''}
          {oficio.registradoEn ? ` · firmado, registrado el ${fecha(oficio.registradoEn)}` : ' · firmado'}
          {destinatario ? ` · dirigido a ${destinatario}` : ''}
        </p>
        {oficio.evidenciaId !== undefined && (
          <Button variant="secondary" size="sm" asChild>
            <a href={urlDeArchivo(oficio.evidenciaId)} target="_blank" rel="noopener noreferrer">
              <FileText size={14} aria-hidden="true" />
              Ver el oficio firmado
            </a>
          </Button>
        )}
      </div>
    )
  }

  const canales = requerimiento.consultas
    .filter((consulta) => !consulta.anulada && consulta.canal !== 'consulta_web')
    .map((consulta) => consulta.canalEtiqueta.toLowerCase())

  return (
    <>
      <Callout
        tone="info"
        title="El oficio sale para numerar y firmar"
        action={
          <div className="sigfisc-acciones">
            <Button variant="secondary" size="sm" asChild>
              <a href={urlDeProyectoOficio(requerimiento.id)} target="_blank" rel="noopener noreferrer">
                <FileText size={14} aria-hidden="true" />
                Descargar el oficio
              </a>
            </Button>
            {puedeRegistrar && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setRegistrando(true)
                }}
              >
                Registrar el oficio firmado
              </Button>
            )}
          </div>
        }
      >
        {destinatario ? `Dirigido a ${destinatario}. ` : ''}Numérelo y fírmelo en el trámite documentario, y registre
        aquí su número y el PDF firmado: es el que se envía. Hasta entonces, el envío
        {canales.length > 0 ? ` por ${canales.join(', ')}` : ''} queda en espera.
      </Callout>

      <DialogoFirmado
        abierto={registrando}
        onCambio={setRegistrando}
        titulo="Registrar el oficio firmado"
        descripcion={`Requerimiento ${String(requerimiento.ordinal)}: el número del trámite reemplaza al provisional ${requerimiento.numeroOficio}.`}
        ejemploNumero="002017-2026-MP-FN-GG-GECPH-GEADPH-SGEADPH"
        etiquetaConfirmar="Registrar el oficio"
        cargando={registrar.isPending}
        onConfirmar={(firmado) => {
          registrar.mutate(
            { requerimientoId: requerimiento.id, ...firmado },
            {
              onSuccess: () => {
                avisarExito(`Oficio N.° ${firmado.numero} registrado.`, 'Ya se puede enviar.')
                setRegistrando(false)
              },
              onError: (error) => {
                avisarError(error, 'No se pudo registrar el oficio firmado')
              },
            },
          )
        }}
      />
    </>
  )
}
